/**
 * Search client factory. Reads window.SEARCH_BACKEND (set by
 * head_scripts.html from the SEARCH_BACKEND Django setting) and returns
 * either an algoliasearch/lite client (hosted) or the ctgSearch client
 * (ctg). The Vue components call this instead of newing up a client
 * directly so the backend can be toggled per environment without a
 * webpack rebuild.
 *
 * Hosted mode's algoliasearch/lite + config imports are LAZY (dynamic
 * import). Webpack splits them into a separate chunk that only loads
 * when createSearchClient() or createAutocompleteClient() is actually
 * called in hosted mode. In ctg mode nothing from algoliasearch/lite
 * or the config module reaches the user's browser.
 *
 * Consequence: these functions are now async. Callers must await or
 * assign the resolved client via a Promise.then / created() hook. See
 * newSearchComponent.vue and SearchInput.vue for the Vue pattern.
 */

import { createCtgSearchClient, INDEX_NAME as CTG_INDEX } from "./index";


function backend() {
  // Default to hosted if the global wasn't injected (e.g. a bundle
  // served from a template that didn't go through head_scripts.html).
  if (typeof window === "undefined") return "hosted";
  const v = window.SEARCH_BACKEND;
  return v === "ctg" ? "ctg" : "hosted";
}


// Cache the dynamic import so repeat calls don't trigger a second
// chunk fetch. The first call starts the fetch; subsequent calls await
// the same Promise.
let _hostedModulesPromise = null;
function loadHostedModules() {
  if (_hostedModulesPromise === null) {
    _hostedModulesPromise = Promise.all([
      import("algoliasearch/lite"),
      import("config"),
    ]).then(([algolia, config]) => ({
      algoliasearch: algolia.default,
      APP_ID: config.ALGOLIA_APP_ID,
      INDEX:  config.ALGOLIA_INDEX,
      KEY:    config.ALGOLIA_API_KEY,
    }));
  }
  return _hostedModulesPromise;
}


export async function createSearchClient() {
  if (backend() === "ctg") {
    return createCtgSearchClient();
  }
  const h = await loadHostedModules();
  return h.algoliasearch(h.APP_ID, h.KEY);
}


/**
 * Autocomplete variant. In ctg mode this returns a client that does
 * exact token matching only (no Damerau-Levenshtein budget), so a
 * mispelled query returns zero suggestions instead of fuzzy guesses --
 * per the explicit decision that autocomplete does NOT typo-correct.
 * In hosted mode this is identical to createSearchClient(); the Algolia
 * index's own typo tolerance still applies there.
 */
export async function createAutocompleteClient() {
  if (backend() === "ctg") {
    return createCtgSearchClient({ exactOnly: true });
  }
  const h = await loadHostedModules();
  return h.algoliasearch(h.APP_ID, h.KEY);
}


export async function getIndexName() {
  if (backend() === "ctg") {
    return CTG_INDEX;
  }
  const h = await loadHostedModules();
  return h.INDEX;
}


export function isCtgBackend() {
  return backend() === "ctg";
}
